import { Dispute } from 'APIs/disputes/disputes.types';
import { CurrencyCode } from 'APIs/fragments';
import { AnyMerchant } from 'APIs/merchants/merchants.types';
import Spinner from 'Components/Spinner/Spinner';
import { TransactionAPIResponseType } from 'Components/Transactions/types';
import { H2, Label, LpBox, Row } from 'Constants/styles';
import { DateTime } from 'luxon';
import { toCurrency } from 'utils/currency';
import getDateFormat from 'utils/getDateFormat';

import { Content, SpinnerWrapper, UpdateDisputeOutcomeBtn } from './styles';
import { SetDisputeOutcomeFormProps } from './types';

type Props = {
  dispute: Dispute;
  payment: TransactionAPIResponseType | null;
  merchant: AnyMerchant | null;
  updateDispute: (formData: SetDisputeOutcomeFormProps) => void;
  setModalDispute: (modalDispute: Dispute | null) => void;
};

const DisputeDetails = ({ dispute, payment, merchant, updateDispute, setModalDispute }: Props) => {
  return (
    <>
      <Content>
        <LpBox>
          <Row>
            <div className="col-9">
              <Row className="mt-4">
                <Label className="col-3">Dispute Id</Label>
                <div className="col-9">{dispute.disputeId}</div>
              </Row>
              <Row className="mt-3">
                <Label className="col-3">Dispute amount</Label>
                <div className="col-9">
                  {toCurrency(dispute.disputedAmount, dispute.currency as CurrencyCode)} {dispute.currency}
                </div>
              </Row>
              <Row className="mt-3">
                <Label className="col-3">Dispute fee</Label>
                <div className="col-9">
                  {toCurrency(dispute.disputeFee, dispute.currency as CurrencyCode)} {dispute.currency}
                </div>
              </Row>
              <Row className="mt-3">
                <Label className="col-3">Description</Label>
                <div className="col-9">{dispute.description || '-'}</div>
              </Row>
              <Row className="mt-3">
                <Label className="col-3">Reason</Label>
                <div className="col-9">{dispute.reason || '-'}</div>
              </Row>
              <Row className="mt-3">
                <Label className="col-3">State</Label>
                <div className="col-9">{dispute.state}</div>
              </Row>
              <Row className="mt-3">
                <Label className="col-3">Outcome</Label>
                <div className="col-9">{dispute.outcome}</div>
              </Row>
              <Row className="mt-3">
                <Label className="col-3">Updated at</Label>
                <div className="col-9">{DateTime.fromISO(dispute.updatedAt).toFormat('dd/LL/yyyy, HH:mm')}</div>
              </Row>
              <Row className="mt-3">
                <Label className="col-3">Created at</Label>
                <div className="col-9">{DateTime.fromISO(dispute.updatedAt).toFormat('dd/LL/yyyy, HH:mm')}</div>
              </Row>
            </div>
            <div className="col-3">
              {dispute.outcome === 'pending' && (
                <UpdateDisputeOutcomeBtn
                  onClick={(e) => {
                    e.preventDefault();
                    setModalDispute(dispute);
                  }}
                >
                  Update outcome
                </UpdateDisputeOutcomeBtn>
              )}
            </div>
          </Row>
        </LpBox>
      </Content>
      <Content>
        <LpBox>
          <H2>Merchant</H2>
          {!merchant && (
            <SpinnerWrapper>
              <Spinner color="#0016D1" />
            </SpinnerWrapper>
          )}
          {merchant && (
            <>
              <Row className="mt-4">
                <Label className="col-3">Merchant Id</Label>
                <div className="col-9">{merchant.merchantId}</div>
              </Row>
              <Row className="mt-3">
                <Label className="col-3">Business name</Label>
                <div className="col-9">{merchant.businessProfile.businessName}</div>
              </Row>
            </>
          )}
        </LpBox>
        <LpBox>
          <H2>Transaction details</H2>
          {!payment && (
            <SpinnerWrapper>
              <Spinner color="#0016D1" />
            </SpinnerWrapper>
          )}
          {payment && (
            <>
              <Row className="mt-4">
                <Label className="col-3">Transaction Id</Label>
                <div className="col-9">{payment?.transactionId}</div>
              </Row>
              <Row className="mt-3">
                <Label className="col-3">Transaction type</Label>
                <div className="col-9">
                  {payment?.transactionPayType.payCardId && 'Full payment'}
                  {payment?.transactionPayType.payPlanId && 'Split payment'}
                </div>
              </Row>
              <Row className="mt-3">
                <Label className="col-3">Transaction amount</Label>
                <div className="col-9">{payment ? toCurrency(payment.transactionAmount, payment.currency) : '-'}</div>
              </Row>
              <Row className="mt-3">
                <Label className="col-3">Currency</Label>
                <div className="col-9">{payment?.currency}</div>
              </Row>
              <Row className="mt-3">
                <Label className="col-3">Transaction status</Label>
                <div className="col-9">{payment?.transactionStatus}</div>
              </Row>
              {payment?.transactionPayType.payPlanId && (
                <Row className="mt-3">
                  <Label className="col-3">Pay plan state</Label>
                  <div className="col-9">{payment?.payPlanState}</div>
                </Row>
              )}
              <Row className="mt-3">
                <Label className="col-3">Payment source brand</Label>
                <div className="col-9">{payment?.paymentSourceBrand ?? '-'}</div>
              </Row>
              <Row className="mt-3">
                <Label className="col-3">Payment source last4</Label>
                <div className="col-9">{payment?.paymentSourceLast4 ?? '-'}</div>
              </Row>
              <Row className="mt-3">
                <Label className="col-3">Platform fee</Label>
                <div className="col-9">{payment ? toCurrency(payment.platformFee, payment.currency) : '-'}</div>
              </Row>
              <Row className="mt-3">
                <Label className="col-3">Refunded amount</Label>
                <div className="col-9">{payment ? toCurrency(payment.refundedAmount, payment.currency) : '-'}</div>
              </Row>
              <Row className="mt-3">
                <Label className="col-3">Late fees paid</Label>
                <div className="col-9">{payment ? toCurrency(payment.payPlanLateFeesPaid, payment.currency) : '-'}</div>
              </Row>
              <Row className="mt-3">
                <Label className="col-3">Late fees accrued</Label>
                <div className="col-9">
                  {payment ? toCurrency(payment.payPlanLateFeesAccrued, payment.currency) : '-'}
                </div>
              </Row>
              {payment?.transactionPayType.payPlanId && (
                <Row className="mt-3">
                  <Label className="col-3">Initial payment amount</Label>
                  <div className="col-9">
                    {payment ? toCurrency(payment.payPlanInitialPaymentAmount, payment.currency) : '-'}
                  </div>
                </Row>
              )}
              {payment?.transactionPayType.payPlanId && (
                <Row className="mt-3">
                  <Label className="col-3">Scheduled instalments</Label>
                  <div className="col-9">
                    {payment.payPlanScheduledInstalments.map((instalment, index) => (
                      <div key={index}>
                        <span>{toCurrency(instalment.amount, payment.currency)}</span>
                        <span className="ml-2">
                          {
                            getDateFormat({
                              time: instalment.dueAt,
                              showComma: false,
                              showFullYear: true,
                              showDaySuffix: false,
                            }).date
                          }
                        </span>
                      </div>
                    ))}
                  </div>
                </Row>
              )}
              <Row className="mt-3">
                <Label className="col-3">Billing address</Label>
                <div className="col-9">
                  <div>{payment?.merchantOrderBilling?.address.line1}</div>
                  <div>{payment?.merchantOrderBilling?.address.line2}</div>
                  <span className="mr-1">{payment?.merchantOrderBilling?.address.city}</span>
                  <span className="mr-1">{payment?.merchantOrderBilling?.address.state}</span>
                  <span className="mr-1">{payment?.merchantOrderBilling?.address.postalCode}</span>
                </div>
              </Row>
              <Row className="mt-3">
                <Label className="col-3">Shipping address</Label>
                <div className="col-9">
                  <div>{payment?.merchantOrderShipping?.address.line1}</div>
                  <div>{payment?.merchantOrderShipping?.address.line2}</div>
                  <span className="mr-1">{payment?.merchantOrderShipping?.address.city}</span>
                  <span className="mr-1">{payment?.merchantOrderShipping?.address.state}</span>
                  <span className="mr-1">{payment?.merchantOrderShipping?.address.postalCode}</span>
                </div>
              </Row>
            </>
          )}
        </LpBox>
        <LpBox>
          <H2>Customer details</H2>
          {!payment && (
            <SpinnerWrapper>
              <Spinner color="#0016D1" />
            </SpinnerWrapper>
          )}
          {payment && (
            <>
              <Row className="mt-4">
                <Label className="col-3">Customer Id</Label>
                <div className="col-9">{payment?.customerId}</div>
              </Row>
              <Row className="mt-3">
                <Label className="col-3">Full name</Label>
                <div className="col-9">{payment?.merchantOrderBilling?.name || payment?.customerFullName || '-'}</div>
              </Row>
              <Row className="mt-3">
                <Label className="col-3">Email</Label>
                <div className="col-9">{payment?.customerEmail}</div>
              </Row>
              <Row className="mt-3">
                <Label className="col-3">Phone number</Label>
                <div className="col-9">{payment?.customerPhoneNumber}</div>
              </Row>
            </>
          )}
        </LpBox>
        <LpBox>
          <H2>Payout details</H2>
          {!payment && (
            <SpinnerWrapper>
              <Spinner color="#0016D1" />
            </SpinnerWrapper>
          )}
          {payment && (
            <>
              <Row className="mt-4">
                <Label className="col-3">Payout Id</Label>
                <div className="col-9">{payment?.payoutId}</div>
              </Row>
              <Row className="mt-3">
                <Label className="col-3">Payout status</Label>
                <div className="col-9">{payment?.payoutStatus}</div>
              </Row>
              <Row className="mt-3">
                <Label className="col-3">Payout arrived at</Label>
                <div className="col-9">
                  {
                    getDateFormat({
                      time: payment?.payoutArrivalAt ?? '',
                      showFullYear: true,
                      showComma: false,
                      showDaySuffix: false,
                    }).formatted
                  }
                </div>
              </Row>
              <Row className="mt-3">
                <Label className="col-3">Payout amount</Label>
                <div className="col-9">{payment ? toCurrency(payment.payoutAmount, payment.currency) : '-'}</div>
              </Row>
            </>
          )}
        </LpBox>
      </Content>
    </>
  );
};

export default DisputeDetails;
